import { redirect, type LoaderFunctionArgs } from 'react-router';
import { Outlet } from 'react-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { trackPage, useBiEvents } from '~/lib/bi-events';
import { getSession } from '~/lib/session.server';
import loginAnimation from './assets/login_animation.gif';
import bgImage from './assets/bg.png';
import bgImageTablet from './assets/dots_tablet.png';
import bgImageMobile from './assets/dots_mobile.png';
import dotsPattern from './assets/dots.png';

const ns_name = 'auth-layout';

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await getSession(request);
  const userId = session.get('user-id');
  if (userId) {
    return redirect('/');
  }

  return {};
}

export default function Auth() {
  const { pageName, pageCategory } = useBiEvents();
  const { t } = useTranslation(ns_name);
  const [bgImageSrc, setBgImageSrc] = React.useState(dotsPattern);

  React.useEffect(() => {
    if (pageName) {
      trackPage(pageCategory, pageName);
    }
  }, [pageCategory, pageName]);

  React.useEffect(() => {
    const updateBackground = () => {
      if (window.matchMedia('(min-width: 1024px)').matches) {
        setBgImageSrc(dotsPattern);
      } else if (window.matchMedia('(min-width: 768px)').matches) {
        setBgImageSrc(bgImageTablet);
      } else {
        setBgImageSrc(bgImageMobile);
      }
    };

    updateBackground();
    window.addEventListener('resize', updateBackground);
    return () => window.removeEventListener('resize', updateBackground);
  }, []);

  return (
    <div className="flex flex-1 flex-row overflow-y-auto">
      <div className="relative flex w-full flex-1 flex-col items-center justify-center overflow-hidden lg:w-[50%]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${bgImageSrc})`,
          }}
        />
        <div className="relative z-10 flex w-full flex-col items-center">
          <header className="mb-6">
            <VennLogo />
          </header>
          <Outlet />
        </div>
      </div>
      <div
        className="my-10 mr-12 hidden flex-1 flex-col items-center justify-center rounded-2xl bg-cover bg-center lg:flex"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}>
        <div className="flex h-[60px] items-center rounded-full shadow-[0px_5px_30px_rgba(0,0,0,0.15)]">
          <img src={loginAnimation} alt={t('alt.loginAnimation')} className="h-full w-auto" />
        </div>
      </div>
    </div>
  );
}

export const handle = {
  i18n: 'auth-layout',
};

function VennLogo() {
  return (
    <svg fill="none" height="14" width="72" viewBox="0 0 72 19" xmlns="http://www.w3.org/2000/svg">
      <path
        clipRule="evenodd"
        d="M34.3028 10.4507C34.3322 10.0362 34.3617 9.5329 34.3617 9.05922C34.3322 3.70067 31.0635 0.0592194 26.293 0.0592194C21.2868 0.0592194 17.812 3.70067 17.812 9.05922C17.812 14.477 21.4341 18.0592 26.5285 18.0592C29.12 18.0592 31.4463 17.4375 33.5371 15.6612L31.6819 12.9967C30.2684 14.1217 28.4721 14.8026 26.7641 14.8026C24.3494 14.8026 22.1997 13.5296 21.6991 10.4507H34.3028ZM30.5629 7.60856H21.7285C22.1114 5.09211 23.5838 3.37501 26.2341 3.40461C28.6193 3.40461 30.1506 4.94409 30.5629 7.60856ZM6.83192 17.6151H11.2491L18.081 0.503307H14.135L9.0994 13.7369L4.06381 0.503307H0L6.83192 17.6151ZM52.653 6.86858V17.6153H48.9131V8.112C48.9131 5.24029 47.4408 3.64161 44.9377 3.64161C42.5524 3.64161 40.5205 5.24029 40.5205 8.17121V17.6449H36.8101V0.533052H40.491V2.63503C41.8456 0.62187 43.9659 0.0889731 45.7033 0.0889731C49.7966 0.0593678 52.653 2.87187 52.653 6.86858ZM72.0001 17.6153V6.86858C72.0001 2.87187 69.1437 0.0593678 65.021 0.0889731C63.2835 0.0889731 61.1633 0.62187 59.8087 2.63503V0.533052H56.1277V17.6449H59.8676V8.17121C59.8676 5.24029 61.8995 3.64161 64.2848 3.64161C66.7878 3.64161 68.2602 5.24029 68.2602 8.112V17.6153H72.0001Z"
        className="fill-grey-400"
        fillRule="evenodd"
      />
    </svg>
  );
}
